import React from 'react';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
  retryCount: number;
  maxRetries?: number;
  componentName?: string;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  retryCount,
  maxRetries = 3,
  componentName = 'Component'
}) => {
  const isMaxRetriesReached = retryCount >= maxRetries;

  return (
    <div style={{
      padding: '24px',
      border: '1px solid #ff6b6b',
      borderRadius: '12px',
      backgroundColor: '#fff5f5',
      margin: '16px',
      fontFamily: 'Arial, sans-serif',
      maxWidth: '500px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <div style={{ textAlign: 'center' }}>
        {/* Error Icon */}
        <div style={{
          fontSize: '48px',
          marginBottom: '16px'
        }}>
          ⚠️
        </div>

        {/* Error Title */}
        <h2 style={{
          color: '#d63031',
          marginBottom: '12px',
          fontSize: '20px',
          fontWeight: 'bold'
        }}>
          {componentName} Error
        </h2>

        {/* Error Message */}
        <p style={{
          color: '#636e72',
          marginBottom: '20px',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          Something went wrong with the {componentName.toLowerCase()}. 
          This has been reported to our team and we're working to fix it.
        </p>

        {/* Development Error Details */}
        {process.env.NODE_ENV === 'development' && (
          <details style={{
            textAlign: 'left',
            marginBottom: '20px',
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '6px',
            border: '1px solid #dee2e6'
          }}>
            <summary style={{
              cursor: 'pointer',
              fontWeight: 'bold',
              color: '#495057',
              fontSize: '13px'
            }}>
              Error Details (Development Only)
            </summary>
            <pre style={{
              marginTop: '12px',
              fontSize: '11px',
              color: '#d63031',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: '#fff',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #e9ecef'
            }}>
              {error.toString()}
            </pre>
          </details>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={resetError}
            disabled={isMaxRetriesReached}
            style={{
              padding: '12px 24px',
              backgroundColor: isMaxRetriesReached ? '#6c757d' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isMaxRetriesReached ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              if (!isMaxRetriesReached) {
                e.currentTarget.style.backgroundColor = '#1565c0';
              }
            }}
            onMouseOut={(e) => {
              if (!isMaxRetriesReached) {
                e.currentTarget.style.backgroundColor = '#1976d2';
              }
            }}
          >
            {isMaxRetriesReached ? 'Max Retries Reached' : `Try Again ${retryCount > 0 ? `(${retryCount}/${maxRetries})` : ''}`}
          </button>

          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#5a6268';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#6c757d';
            }}
          >
            Reload Page
          </button>
        </div>

        {/* Retry Status */}
        {retryCount > 0 && (
          <p style={{
            color: retryCount >= maxRetries ? '#e17055' : '#74b9ff',
            fontSize: '12px',
            marginTop: '16px',
            fontStyle: 'italic'
          }}>
            {retryCount >= maxRetries 
              ? 'Multiple retry attempts failed. Please reload the page or contact support.'
              : `Retry attempt ${retryCount} of ${maxRetries}`
            }
          </p>
        )}

        {/* Support Information */}
        <div style={{
          marginTop: '20px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <p style={{
            color: '#6c757d',
            fontSize: '12px',
            margin: '0'
          }}>
            If this problem persists, please contact support with error ID: 
            <code style={{
              backgroundColor: '#e9ecef',
              padding: '2px 4px',
              borderRadius: '3px',
              fontFamily: 'monospace',
              marginLeft: '4px'
            }}>
              {Date.now().toString(36)}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
